import { useCallback, useEffect, useRef, useState } from "react";
import {
    createAdminLot,
    deleteAdminLot,
    searchAdminLots,
    searchAdminProducts,
    updateAdminLot
} from "@/api/admin.api.js";
import { useAuth } from "@/auth/AuthContext.jsx";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll.js";
import { formatLotStatus, lotStatusClassName } from "@/utils/format.js";
import Price from "@/components/Price.jsx";
import { LOT_STATUSES } from "@/utils/constants.js";
import { EMPTY_LOT_FILTERS } from "@/utils/lotFilters.js";
import { EMPTY_PRODUCT_FILTERS } from "@/utils/productFilters.js";
import LotFilterToolbox from "@/components/LotFilterToolbox.jsx";
import ConfirmDialog from "@/components/ConfirmDialog.jsx";
import RowActionsMenu from "@/components/RowActionsMenu.jsx";
import BackLink from "@/components/BackLink.jsx";
import { canDeleteLot, canSeeCostPrice, canWriteLot } from "@/utils/staffAccess.js";

const emptyLotForm = {
    productId: "",
    productTitle: "",
    name: "",
    description: "",
    costPrice: "0",
    quantity: "",
    receivedDate: new Date().toISOString().slice(0, 10),
    status: "READY"
};

function formatLotDate(value) {
    if (!value) {
        return "—";
    }
    const date = typeof value === "string" && value.length === 10
        ? new Date(`${value}T00:00:00`)
        : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }
    return date.toLocaleDateString("fr-FR");
}

function toLotForm(lot) {
    return {
        productId: lot.productId || "",
        productTitle: lot.productTitle || "",
        name: lot.name || "",
        description: lot.description || "",
        costPrice: lot.costPrice ?? "0",
        quantity: lot.remainingQuantity ?? "",
        receivedDate: lot.receivedDate || new Date().toISOString().slice(0, 10),
        status: lot.status || "READY"
    };
}

function isLotUnused(lot) {
    return lot
        && Number(lot.remainingQuantity) === Number(lot.initialQuantity)
        && lot.status !== "DELETED";
}

export default function AdminLotsSection({ setMessage, setError }) {
    const { role } = useAuth();
    const showCostPrice = canSeeCostPrice(role);
    const allowDelete = canDeleteLot(role);
    const allowWriteLot = canWriteLot(role);
    const [lots, setLots] = useState([]);
    const [draftFilters, setDraftFilters] = useState(EMPTY_LOT_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(EMPTY_LOT_FILTERS);
    const appliedFiltersRef = useRef(appliedFilters);
    const [page, setPage] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [viewMode, setViewMode] = useState("list");
    const [selectedLot, setSelectedLot] = useState(null);
    const [form, setForm] = useState(emptyLotForm);
    const [saving, setSaving] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState(null);
    const [productQuery, setProductQuery] = useState("");
    const [productSuggestions, setProductSuggestions] = useState([]);
    const [productSearchLoading, setProductSearchLoading] = useState(false);

    useEffect(() => {
        appliedFiltersRef.current = appliedFilters;
    }, [appliedFilters]);

    const fetchLotsPage = useCallback(async (nextPage, filters, append) => {
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        try {
            const response = await searchAdminLots(filters, nextPage);
            const nextLots = response.data || [];
            setLots((current) => (append ? [...current, ...nextLots] : nextLots));
            setPage(nextPage);
            setHasNext(!!response.hasNext);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [setError]);

    const loadSection = useCallback(async () => {
        setError("");
        await fetchLotsPage(0, appliedFiltersRef.current, false);
    }, [fetchLotsPage, setError]);

    useEffect(() => {
        loadSection();
    }, [loadSection]);

    const applyFilters = async () => {
        const nextFilters = { ...draftFilters };
        setAppliedFilters(nextFilters);
        await fetchLotsPage(0, nextFilters, false);
    };

    const resetFilters = async () => {
        setDraftFilters(EMPTY_LOT_FILTERS);
        setAppliedFilters(EMPTY_LOT_FILTERS);
        await fetchLotsPage(0, EMPTY_LOT_FILTERS, false);
    };

    const loadMore = useCallback(() => {
        fetchLotsPage(page + 1, appliedFiltersRef.current, true);
    }, [fetchLotsPage, page]);

    const sentinelRef = useInfiniteScroll({
        hasNext: viewMode === "list" && hasNext,
        loading,
        loadingMore,
        onLoadMore: loadMore
    });

    const searchProducts = useCallback(async (query) => {
        const term = (query || "").trim();
        if (term.length < 1) {
            setProductSuggestions([]);
            return;
        }
        setProductSearchLoading(true);
        try {
            const response = await searchAdminProducts({
                ...EMPTY_PRODUCT_FILTERS,
                search: term
            }, 0);
            setProductSuggestions(response.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setProductSearchLoading(false);
        }
    }, [setError]);

    useEffect(() => {
        if (viewMode !== "create") {
            return undefined;
        }
        const handle = window.setTimeout(() => {
            searchProducts(productQuery);
        }, 250);
        return () => window.clearTimeout(handle);
    }, [productQuery, searchProducts, viewMode]);

    const openLotView = (lot) => {
        setSelectedLot(lot);
        setViewMode("view");
        setError("");
        setMessage("");
    };

    const startCreate = () => {
        setSelectedLot(null);
        setForm({
            ...emptyLotForm,
            receivedDate: new Date().toISOString().slice(0, 10)
        });
        setProductQuery("");
        setProductSuggestions([]);
        setViewMode("create");
        setError("");
        setMessage("");
    };

    const startEdit = (lot) => {
        const target = lot || selectedLot;
        if (!target) {
            return;
        }
        setSelectedLot(target);
        setForm(toLotForm(target));
        setViewMode("edit");
        setError("");
        setMessage("");
    };

    const backToList = () => {
        setViewMode("list");
        setSelectedLot(null);
        setForm(emptyLotForm);
        setProductQuery("");
        setProductSuggestions([]);
        setError("");
    };

    const cancelForm = () => {
        if (selectedLot && viewMode === "edit") {
            setViewMode("view");
            setForm(toLotForm(selectedLot));
        } else {
            backToList();
        }
        setError("");
    };

    const refreshAfterMutation = async (nextLotId = null) => {
        await fetchLotsPage(0, appliedFiltersRef.current, false);
        if (nextLotId) {
            const response = await searchAdminLots(appliedFiltersRef.current, 0);
            const nextLots = response.data || [];
            const next = nextLots.find((lot) => lot.id === nextLotId) || selectedLot;
            if (next && next.id === nextLotId) {
                setSelectedLot(next);
                setViewMode("view");
                return;
            }
        }
        backToList();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setMessage("");
        setError("");
        try {
            if (viewMode === "edit" && selectedLot) {
                await updateAdminLot(selectedLot.id, {
                    name: form.name.trim(),
                    description: form.description.trim() || undefined,
                    ...(showCostPrice
                        ? { costPrice: form.costPrice === "" || form.costPrice == null ? 0 : Number(form.costPrice) }
                        : {}),
                    remainingQuantity: Number(form.quantity),
                    receivedDate: form.receivedDate || undefined,
                    status: form.status
                });
                setMessage("Lot mis à jour");
                await refreshAfterMutation(selectedLot.id);
            } else {
                if (!form.productId) {
                    throw new Error("Sélectionnez un produit pour ce lot");
                }
                const created = await createAdminLot({
                    productId: form.productId,
                    name: form.name.trim(),
                    description: form.description.trim() || undefined,
                    ...(showCostPrice
                        ? { costPrice: form.costPrice === "" || form.costPrice == null ? 0 : Number(form.costPrice) }
                        : {}),
                    quantity: Number(form.quantity),
                    receivedDate: form.receivedDate || undefined,
                    status: form.status
                });
                setMessage("Lot ajouté");
                await refreshAfterMutation(created.data?.id || null);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!pendingDeleteId) {
            return;
        }
        const deletedId = pendingDeleteId;
        try {
            await deleteAdminLot(deletedId);
            setPendingDeleteId(null);
            setMessage("Lot supprimé");
            await fetchLotsPage(0, appliedFiltersRef.current, false);
            if (selectedLot?.id === deletedId) {
                backToList();
            }
        } catch (err) {
            setPendingDeleteId(null);
            setError(err.message);
        }
    };

    const buildLotMenuItems = (lot, { includeView = true } = {}) => {
        const items = [];
        if (includeView) {
            items.push({
                id: "view",
                label: "Voir le détail",
                onClick: () => openLotView(lot)
            });
        }
        if (lot.status !== "DELETED" && allowWriteLot) {
            items.push({
                id: "edit",
                label: "Modifier",
                onClick: () => startEdit(lot)
            });
        }
        if (allowDelete && isLotUnused(lot)) {
            items.push({
                id: "delete",
                label: "Supprimer",
                danger: true,
                onClick: () => setPendingDeleteId(lot.id)
            });
        }
        return items;
    };

    const pendingDelete = lots.find((lot) => lot.id === pendingDeleteId)
        || (selectedLot?.id === pendingDeleteId ? selectedLot : null);

    const deleteConfirmDialog = (
        <ConfirmDialog
            open={!!pendingDeleteId}
            title="Supprimer le lot"
            message={
                pendingDelete
                    ? `Voulez-vous vraiment supprimer le lot « ${pendingDelete.name} » ?`
                    : "Voulez-vous vraiment supprimer ce lot ?"
            }
            confirmLabel="Supprimer"
            dangerous
            onConfirm={handleDelete}
            onCancel={() => setPendingDeleteId(null)}
        />
    );

    if (viewMode === "create" || viewMode === "edit") {
        return (
            <section className="card profile-section profile-section--catalog">
                <div className="section-header">
                    <h2>{viewMode === "edit" ? "Modifier le lot" : "Ajouter un lot"}</h2>
                </div>
                <form className="admin-lot-form" onSubmit={handleSubmit}>
                    {viewMode === "create" ? (
                        <label className="field">
                            Produit
                            <input
                                type="search"
                                placeholder="Rechercher un produit…"
                                value={productQuery}
                                onChange={(e) => {
                                    setProductQuery(e.target.value);
                                    if (form.productId) {
                                        setForm((current) => ({
                                            ...current,
                                            productId: "",
                                            productTitle: ""
                                        }));
                                    }
                                }}
                                required={!form.productId}
                            />
                            {form.productId ? (
                                <p className="muted">
                                    Sélectionné : <strong>{form.productTitle || form.productId}</strong>
                                </p>
                            ) : null}
                            {productSearchLoading && <p className="muted">Recherche…</p>}
                            {!productSearchLoading && productSuggestions.length > 0 && !form.productId && (
                                <div className="admin-table">
                                    {productSuggestions.slice(0, 8).map((product) => (
                                        <button
                                            key={product.id}
                                            type="button"
                                            className="admin-row admin-list-row-main"
                                            onClick={() => {
                                                setForm((current) => ({
                                                    ...current,
                                                    productId: product.id,
                                                    productTitle: product.title
                                                }));
                                                setProductQuery(product.title);
                                                setProductSuggestions([]);
                                            }}
                                        >
                                            <div className="admin-row-body">
                                                <strong>{product.title}</strong>
                                                <p className="muted">{product.category}</p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </label>
                    ) : (
                        <p className="muted">
                            Produit : <strong>{form.productTitle || "—"}</strong>
                        </p>
                    )}
                    <label className="field">
                        Nom
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            required
                            maxLength={255}
                        />
                    </label>
                    <label className="field">
                        Description
                        <textarea
                            rows="2"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            maxLength={2000}
                        />
                    </label>
                    {showCostPrice && (
                        <label className="field field--amount">
                            Prix de revient (FCFA)
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.costPrice}
                                onChange={(e) => setForm({ ...form, costPrice: e.target.value })}
                            />
                        </label>
                    )}
                    <label className="field field--qty">
                        {viewMode === "edit" ? "Quantité restante" : "Quantité"}
                        <input
                            type="number"
                            min={viewMode === "edit" ? "0" : "1"}
                            step="1"
                            value={form.quantity}
                            onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                            required
                        />
                    </label>
                    <label className="field field--date">
                        Date de réception
                        <input
                            type="date"
                            value={form.receivedDate}
                            onChange={(e) => setForm({ ...form, receivedDate: e.target.value })}
                            required
                        />
                    </label>
                    <label className="field field--status">
                        Statut
                        <select
                            value={form.status}
                            onChange={(e) => setForm({ ...form, status: e.target.value })}
                        >
                            {LOT_STATUSES.filter((status) => status !== "DELETED" || viewMode === "edit").map((status) => (
                                <option key={status} value={status}>{formatLotStatus(status)}</option>
                            ))}
                        </select>
                    </label>
                    <div className="profile-form-actions">
                        <button className="btn btn-primary btn-inline" type="submit" disabled={saving}>
                            {saving
                                ? "Enregistrement..."
                                : (viewMode === "edit" ? "Enregistrer" : "Ajouter le lot")}
                        </button>
                        <button type="button" className="btn btn-secondary btn-inline" onClick={cancelForm}>
                            Annuler
                        </button>
                    </div>
                </form>
                {deleteConfirmDialog}
            </section>
        );
    }

    if (viewMode === "view" && selectedLot) {
        return (
            <section className="card profile-section profile-section--catalog">
                <div className="section-header">
                    <div>
                        <BackLink onClick={backToList} className="purchase-detail-back">
                            Retour aux lots
                        </BackLink>
                        <h2>Détails du lot</h2>
                    </div>
                    <RowActionsMenu
                        ariaLabel={`Actions pour ${selectedLot.name}`}
                        items={buildLotMenuItems(selectedLot, { includeView: false })}
                    />
                </div>

                <dl className="profile-detail-view profile-detail-view--compact">
                    <div className="profile-detail-row">
                        <dt>Nom</dt>
                        <dd>{selectedLot.name}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Produit</dt>
                        <dd>{selectedLot.productTitle || "—"}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Description</dt>
                        <dd>{selectedLot.description || "—"}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Statut</dt>
                        <dd>
                            <span className={lotStatusClassName(selectedLot.status)}>
                                {formatLotStatus(selectedLot.status)}
                            </span>
                        </dd>
                    </div>
                    {showCostPrice && (
                        <div className="profile-detail-row">
                            <dt>Prix de revient</dt>
                            <dd><Price value={selectedLot.costPrice ?? 0} /></dd>
                        </div>
                    )}
                    <div className="profile-detail-row">
                        <dt>Quantité initiale</dt>
                        <dd>{selectedLot.initialQuantity}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Quantité restante</dt>
                        <dd>{selectedLot.remainingQuantity}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Date de réception</dt>
                        <dd>{formatLotDate(selectedLot.receivedDate)}</dd>
                    </div>
                </dl>
                {deleteConfirmDialog}
            </section>
        );
    }

    return (
        <section className="card profile-section profile-section--catalog">
            <div className="catalog-with-filters">
                <LotFilterToolbox
                    filters={draftFilters}
                    loading={loading && !loadingMore}
                    onChange={setDraftFilters}
                    onApply={applyFilters}
                    onReset={resetFilters}
                />

                <div className="catalog-results">
                    <div className="catalog-results-header">
                        <div className="catalog-results-header-main">
                            <h2>Lots</h2>
                            {allowWriteLot && (
                                <button
                                    type="button"
                                    className="btn btn-primary btn-inline catalog-results-create-btn"
                                    onClick={startCreate}
                                >
                                    Ajouter un lot
                                </button>
                            )}
                        </div>
                        {!loading && (
                            <p className="muted catalog-results-count">
                                {lots.length} lot{lots.length > 1 ? "s" : ""} affiché{lots.length > 1 ? "s" : ""}
                                {hasNext ? " · faites défiler pour en voir plus" : ""}
                            </p>
                        )}
                    </div>

                    {loading && <p className="muted">Chargement des lots...</p>}

                    {!loading && lots.length === 0 && (
                        <p className="muted">Aucun lot ne correspond à votre recherche.</p>
                    )}

                    <div className="admin-table">
                        {lots.map((lot) => (
                            <div key={lot.id} className="admin-row admin-list-row">
                                <button
                                    type="button"
                                    className="admin-list-row-main"
                                    onClick={() => openLotView(lot)}
                                >
                                    <div className="admin-row-body">
                                        <strong>{lot.name}</strong>
                                        <p className="muted">
                                            {lot.productTitle || "Produit"}
                                            {" · "}
                                            <span className={lotStatusClassName(lot.status)}>
                                                {formatLotStatus(lot.status)}
                                            </span>
                                            {" · "}
                                            {lot.remainingQuantity}/{lot.initialQuantity} articles
                                            {showCostPrice && (
                                                <>
                                                    {" · "}
                                                    prix de revient <Price value={lot.costPrice ?? 0} />
                                                </>
                                            )}
                                        </p>
                                    </div>
                                    <span className="admin-list-row-chevron" aria-hidden="true">›</span>
                                </button>
                                <RowActionsMenu
                                    ariaLabel={`Actions pour ${lot.name}`}
                                    items={buildLotMenuItems(lot)}
                                />
                            </div>
                        ))}
                    </div>

                    {loadingMore && <p className="muted catalog-loading-more">Chargement de la suite...</p>}
                    <div ref={sentinelRef} className="catalog-scroll-sentinel" aria-hidden="true" />
                </div>
            </div>
            {deleteConfirmDialog}
        </section>
    );
}
